import { useCallback, useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import Select, { SingleValue } from "react-select";
import { useTranslation } from "react-i18next";
import styles from "./completedTransfersV2.module.scss";
import {
  COMPLETED_TRANSFERS_TAB,
  HEADER,
  STATUS_FILTER_ALL,
  STATUS_FILTER_OPTIONS,
  TABLE_HEADERS,
} from "./constants";
import SearchInput from "../../components/common/searchInput";
import CompletedTransfersV2TableShimmer from "./CompletedTransfersV2TableShimmer";
import CompletedTransfersV2ListItem from "./CompletedTransfersV2ListItem";
import Paginations from "../../components/common/Pagination";
import { getCompletedTransfers } from "../../setup/api/services/completed-transfers";
import { ICompletedTransferBody } from "../../setup/api/services/completed-transfers/models";
import { TransactionStatus } from "../../setup/api/services/contract/models";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

type StatusOption = (typeof STATUS_FILTER_OPTIONS)[number];

const CompletedTransfersV2 = () => {
  const { t } = useTranslation();

  const [transfers, setTransfers] = useState<ICompletedTransferBody[]>([]);
  const [userIdSearchValue, setUserIdSearchValue] = useState("");
  const [transactionIdSearchValue, setTransactionIdSearchValue] = useState("");
  // Debounced mirrors of the search inputs — these are what the request uses.
  const [appliedUserId, setAppliedUserId] = useState("");
  const [appliedTransactionId, setAppliedTransactionId] = useState("");
  const [status, setStatus] = useState<StatusOption>(STATUS_FILTER_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAppliedUserId(userIdSearchValue);
      setAppliedTransactionId(transactionIdSearchValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [userIdSearchValue, transactionIdSearchValue]);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCompletedTransfers({
        beneficiaryId: appliedUserId,
        transactionId: appliedTransactionId,
        status:
          status.value === STATUS_FILTER_ALL
            ? undefined
            : (status.value as TransactionStatus),
        page: page - 1,
        size: PAGE_SIZE,
      });
      setTransfers(response.data.content);
      setPagesCount(response.data.totalPages);
    } catch (error) {
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [appliedUserId, appliedTransactionId, status, page]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleUserIdSearchValue = useCallback((event) => {
    // The two searches are mutually exclusive, as in the original tab.
    setTransactionIdSearchValue("");
    setUserIdSearchValue(event.target.value);
    setPage(1);
  }, []);

  const handleTransactionIdSearchValue = useCallback((event) => {
    setUserIdSearchValue("");
    setTransactionIdSearchValue(event.target.value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback(
    (option: SingleValue<StatusOption>) => {
      setStatus(option ?? STATUS_FILTER_OPTIONS[0]);
      setPage(1);
    },
    []
  );

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  return (
    <div className={styles["page_layout"]}>
      <h1>{HEADER}</h1>
      <div className={styles.header_container}>
        <div className={styles["right_section"]}>
          <Tabs
            style={{ alignItems: "center" }}
            className={styles.tab_wrapper}
            value={"completed"}>
            <Tab
              value="completed"
              label={COMPLETED_TRANSFERS_TAB}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
          </Tabs>
          <Select
            value={status}
            options={STATUS_FILTER_OPTIONS}
            onChange={handleStatusChange}
            isSearchable={false}
            styles={{
              control: (base) => ({
                ...base,
                width: 200,
                height: "3rem",
                padding: "0 1rem",
                border: "1px solid #E2E8EE",
                borderRadius: "6rem",
              }),
            }}
          />
        </div>
        <div className={styles["left_section"]}>
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_user_id")}
            value={userIdSearchValue}
            style={{ height: "35px", width: "252px", marginLeft: "10px" }}
            handleChange={handleUserIdSearchValue}
          />
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_transaction_id")}
            value={transactionIdSearchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={handleTransactionIdSearchValue}
          />
        </div>
      </div>

      <div className={styles.table_scroll}>
        <table className={styles.table}>
          <tr className={styles.table_header}>
            <th className={styles.hint}>{TABLE_HEADERS.USERNAME}</th>
            <th className={styles.hint}>{TABLE_HEADERS.BENEFICIARY_ID}</th>
            <th className={styles.hint}>{TABLE_HEADERS.REQUESTER_NAME}</th>
            <th className={styles.hint}>{TABLE_HEADERS.REQUESTER_ID}</th>
            <th className={styles.hint}>{TABLE_HEADERS.REQUESTER_TYPE}</th>
            <th className={styles.hint}>{TABLE_HEADERS.STATUS}</th>
            <th className={styles.hint}>{TABLE_HEADERS.DOMAIN_ID}</th>
            <th className={styles.hint}>{TABLE_HEADERS.TRANSACTION_ID}</th>
            <th className={styles.hint}>{TABLE_HEADERS.GATEWAY_PAYMENT_ID}</th>
            <th className={styles.hint}>
              {TABLE_HEADERS.REFERENCE_TRANSACTION_ID}
            </th>
            <th className={styles.hint}>{TABLE_HEADERS.AMOUNT}</th>
            <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_CHANNEL}</th>
            <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_TYPE}</th>
            <th className={styles.hint}>{TABLE_HEADERS.MANUAL_WITHDRAW}</th>
            <th className={styles.hint}>{TABLE_HEADERS.CREATED_AT}</th>
            <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_TIME}</th>
          </tr>
          {loading ? (
            <CompletedTransfersV2TableShimmer />
          ) : (
            <tbody className={styles.table_body}>
              {transfers?.map((transfer, index) => (
                <CompletedTransfersV2ListItem
                  key={transfer.id ?? index}
                  transfer={transfer}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      <>
        <div className={transfers?.length ? "paginatorContainer" : "hide"}>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};

export default CompletedTransfersV2;
